import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const sections = [
  { id: 'responsable', label: '1. Responsable du traitement' },
  { id: 'donnees-collectees', label: '2. Données collectées' },
  { id: 'finalites', label: '3. Finalités' },
  { id: 'base-legale', label: '4. Base légale' },
  { id: 'conservation', label: '5. Durée de conservation' },
  { id: 'droits', label: '6. Droits des utilisateurs' },
  { id: 'sous-traitants', label: '7. Sous-traitants' },
  { id: 'cookies', label: '8. Cookies' },
  { id: 'transferts', label: '9. Transferts de données' },
  { id: 'securite', label: '10. Sécurité' },
  { id: 'modifications', label: '11. Modifications' },
  { id: 'contact-dpo', label: '12. Contact DPO' },
];

const Confidentialite = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('responsable');

  useEffect(() => {
    const handleScroll = () => {
      const offsets = sections.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });
      const current = offsets.filter((o) => o.top <= 120).pop();
      if (current) setActiveSection(current.id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-nexus-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">
            Politique de Confidentialité
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Dernière mise à jour : 17 avril 2026
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-10">
          {/* Sidebar navigation (desktop only) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              {sections.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === id
                      ? 'bg-nexus-50 text-nexus-700 font-medium'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {activeSection === id && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                  <span>{label}</span>
                </a>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 lg:p-10">
              <div className="prose prose-slate max-w-none prose-headings:scroll-mt-24">
                <p className="text-slate-600 leading-relaxed">
                  La présente Politique de Confidentialité décrit la manière dont <strong>ACL Gestion</strong>{' '}
                  (ci-après « nous », « notre » ou « ACL Gestion ») collecte, utilise, stocke et protège les
                  données personnelles des utilisateurs de la plateforme <strong>Nexus-360</strong>. Cette
                  politique est établie conformément au Règlement (UE) 2016/679 du 27 avril 2016 (RGPD) et à
                  la loi n° 78-17 du 6 janvier 1978 modifiée (loi Informatique et Libertés).
                </p>

                {/* 1. Responsable du Traitement */}
                <h2 id="responsable" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  1. Responsable du Traitement
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Le responsable du traitement des données personnelles collectées via la Plateforme Nexus-360 est :
                </p>
                <div className="bg-slate-50 rounded-lg p-4 mt-3 text-slate-600">
                  <p><strong>ACL Gestion</strong></p>
                  <p>Adresse : Paris, France</p>
                  <p>E-mail : <a href="mailto:contact@nexus-360.fr" className="text-nexus-600 hover:underline">contact@nexus-360.fr</a></p>
                </div>

                {/* 2. Données Personnelles Collectées */}
                <h2 id="donnees-collectees" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  2. Données Personnelles Collectées
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Dans le cadre de l'utilisation de la Plateforme, nous sommes amenés à collecter les
                  catégories de données personnelles suivantes :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li>
                    <strong>Données d'identification :</strong> nom, prénom, adresse e-mail professionnelle.
                  </li>
                  <li>
                    <strong>Données relatives à la structure :</strong> raison sociale, numéro SIRET, adresse
                    du siège social, secteur d'activité.
                  </li>
                  <li>
                    <strong>Données de connexion :</strong> adresse IP, type de navigateur, système
                    d'exploitation, dates et heures de connexion.
                  </li>
                  <li>
                    <strong>Données d'usage :</strong> pages consultées, fonctionnalités utilisées, durée des
                    sessions, actions effectuées sur la Plateforme.
                  </li>
                  <li>
                    <strong>Données de facturation :</strong> informations de paiement (traitées directement par
                    Stripe, nous ne stockons pas les numéros de carte bancaire), historique des factures,
                    montants et dates de paiement.
                  </li>
                </ul>

                {/* 3. Finalités du Traitement */}
                <h2 id="finalites" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  3. Finalités du Traitement
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Les données personnelles collectées sont traitées pour les finalités suivantes :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li><strong>Gestion du compte utilisateur :</strong> création, authentification, administration du compte et des droits d'accès.</li>
                  <li><strong>Fourniture du service :</strong> mise à disposition des fonctionnalités de la Plateforme, personnalisation de l'expérience utilisateur.</li>
                  <li><strong>Facturation et paiement :</strong> gestion des abonnements, émission des factures, traitement des paiements.</li>
                  <li><strong>Support client :</strong> traitement des demandes d'assistance, résolution des incidents techniques.</li>
                  <li><strong>Amélioration du service :</strong> analyse statistique anonymisée de l'usage de la Plateforme, identification des axes d'amélioration, correction des dysfonctionnements.</li>
                  <li><strong>Communication :</strong> envoi de notifications relatives au service (mises à jour, maintenance, alertes de sécurité). Aucun e-mail commercial n'est envoyé sans consentement explicite.</li>
                </ul>

                {/* 4. Base Légale */}
                <h2 id="base-legale" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  4. Base Légale du Traitement
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Chaque traitement de données personnelles repose sur l'une des bases légales suivantes,
                  conformément à l'article 6 du RGPD :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li>
                    <strong>Exécution du contrat (art. 6.1.b) :</strong> les traitements nécessaires à la
                    fourniture des Services dans le cadre de l'Abonnement souscrit (gestion du compte,
                    fourniture des fonctionnalités, facturation).
                  </li>
                  <li>
                    <strong>Consentement (art. 6.1.a) :</strong> les traitements fondés sur le consentement
                    préalable de l'Utilisateur, notamment pour l'envoi de communications commerciales.
                    L'Utilisateur peut retirer son consentement à tout moment.
                  </li>
                  <li>
                    <strong>Intérêt légitime (art. 6.1.f) :</strong> les traitements nécessaires aux intérêts
                    légitimes d'ACL Gestion, tels que l'amélioration de la Plateforme, la prévention de la
                    fraude et la sécurité des systèmes, sous réserve de ne pas porter atteinte de manière
                    disproportionnée aux droits et libertés des Utilisateurs.
                  </li>
                  <li>
                    <strong>Obligation légale (art. 6.1.c) :</strong> les traitements imposés par la
                    réglementation applicable, notamment en matière de conservation des données comptables
                    et fiscales.
                  </li>
                </ul>

                {/* 5. Durée de Conservation */}
                <h2 id="conservation" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  5. Durée de Conservation
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Les données personnelles sont conservées pour une durée proportionnée à la finalité du
                  traitement :
                </p>
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm text-slate-600 border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-900">Type de données</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-900">Durée de conservation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4">Données de compte actif</td>
                        <td className="py-3 px-4">Durée du contrat + 3 ans après la résiliation</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4">Données comptables et de facturation</td>
                        <td className="py-3 px-4">10 ans (obligation légale, art. L.123-22 du Code de commerce)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4">Données de connexion (logs)</td>
                        <td className="py-3 px-4">1 an (obligation légale, décret n° 2011-219)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4">Cookies fonctionnels</td>
                        <td className="py-3 px-4">Durée de la session</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">Données de prospection commerciale</td>
                        <td className="py-3 px-4">3 ans à compter du dernier contact</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-600 leading-relaxed mt-3">
                  À l'expiration de ces délais, les données sont supprimées ou anonymisées de manière
                  irréversible.
                </p>

                {/* 6. Droits des Utilisateurs */}
                <h2 id="droits" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  6. Droits des Utilisateurs
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Conformément au RGPD et à la loi Informatique et Libertés, chaque Utilisateur dispose des
                  droits suivants sur ses données personnelles :
                </p>
                <ul className="space-y-3 text-slate-600 mt-3">
                  <li>
                    <strong>Droit d'accès (art. 15 RGPD) :</strong> obtenir la confirmation que des données
                    personnelles vous concernant sont traitées et en obtenir une copie.
                  </li>
                  <li>
                    <strong>Droit de rectification (art. 16 RGPD) :</strong> demander la correction des données
                    inexactes ou incomplètes.
                  </li>
                  <li>
                    <strong>Droit à l'effacement (art. 17 RGPD) :</strong> demander la suppression de vos
                    données personnelles, sous réserve des obligations légales de conservation.
                  </li>
                  <li>
                    <strong>Droit à la portabilité (art. 20 RGPD) :</strong> recevoir vos données dans un
                    format structuré, couramment utilisé et lisible par machine (JSON, CSV).
                  </li>
                  <li>
                    <strong>Droit d'opposition (art. 21 RGPD) :</strong> vous opposer au traitement de vos
                    données pour des motifs légitimes, notamment pour les traitements fondés sur l'intérêt
                    légitime.
                  </li>
                  <li>
                    <strong>Droit à la limitation (art. 18 RGPD) :</strong> demander la limitation du
                    traitement de vos données dans les cas prévus par le RGPD.
                  </li>
                  <li>
                    <strong>Droit de retirer le consentement :</strong> lorsque le traitement est fondé sur le
                    consentement, vous pouvez le retirer à tout moment sans que cela n'affecte la licéité du
                    traitement antérieur.
                  </li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Pour exercer ces droits, vous pouvez nous contacter à l'adresse{' '}
                  <a href="mailto:contact@nexus-360.fr" className="text-nexus-600 hover:underline font-medium">
                    contact@nexus-360.fr
                  </a>
                  . Nous nous engageons à répondre dans un délai maximum d'un (1) mois à compter de la
                  réception de votre demande. Ce délai peut être prolongé de deux (2) mois en cas de demande
                  complexe ou de nombre élevé de demandes.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  En cas de réponse insatisfaisante, vous disposez du droit d'introduire une réclamation
                  auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :{' '}
                  <a
                    href="https://www.cnil.fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-nexus-600 hover:underline"
                  >
                    www.cnil.fr
                  </a>
                  .
                </p>

                {/* 7. Sous-traitants */}
                <h2 id="sous-traitants" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  7. Sous-traitants
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Afin d'assurer le bon fonctionnement de la Plateforme, nous faisons appel aux
                  sous-traitants suivants, sélectionnés pour leur conformité au RGPD :
                </p>
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm text-slate-600 border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-900">Sous-traitant</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-900">Fonction</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-900">Localisation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4 font-medium">OVH SAS</td>
                        <td className="py-3 px-4">Hébergement de l'infrastructure et des données</td>
                        <td className="py-3 px-4">France (Roubaix, Gravelines)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 px-4 font-medium">Stripe, Inc.</td>
                        <td className="py-3 px-4">Traitement des paiements par carte bancaire</td>
                        <td className="py-3 px-4">États-Unis / Irlande (UE)</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium">Resend, Inc.</td>
                        <td className="py-3 px-4">Envoi d'e-mails transactionnels</td>
                        <td className="py-3 px-4">États-Unis</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Chaque sous-traitant est lié par un accord de traitement de données (DPA) conforme à
                  l'article 28 du RGPD, garantissant un niveau de protection adéquat des données personnelles.
                </p>

                {/* 8. Cookies */}
                <h2 id="cookies" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  8. Cookies
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  La Plateforme Nexus-360 utilise exclusivement des <strong>cookies strictement nécessaires</strong>{' '}
                  au fonctionnement du service. Ces cookies sont indispensables à la navigation et ne
                  nécessitent pas le consentement préalable de l'Utilisateur conformément à l'article 82 de la
                  loi Informatique et Libertés.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Les cookies utilisés sont les suivants :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li><strong>Jeton d'authentification (JWT) :</strong> permet de maintenir la session de l'Utilisateur connecté.</li>
                  <li><strong>Préférences de langue :</strong> mémorise la langue choisie par l'Utilisateur (français ou anglais).</li>
                  <li><strong>Identifiant de session :</strong> assure le bon fonctionnement des requêtes entre le navigateur et le serveur.</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  <strong>Aucun cookie de suivi, de publicité ou d'analyse comportementale</strong> n'est
                  déposé sur le terminal de l'Utilisateur. Nous n'utilisons pas Google Analytics ni aucun
                  autre outil de tracking tiers.
                </p>

                {/* 9. Transferts de Données */}
                <h2 id="transferts" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  9. Transferts de Données Hors Union Européenne
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Les données personnelles sont principalement hébergées et traitées au sein de l'Union
                  européenne (France), chez notre hébergeur OVH SAS.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Toutefois, certains de nos sous-traitants (Stripe, Resend) peuvent être amenés à transférer
                  des données vers les États-Unis. Ces transferts sont encadrés par les mécanismes de
                  protection suivants :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li><strong>Stripe :</strong> certifié EU-US Data Privacy Framework. Des clauses contractuelles types (CCT) adoptées par la Commission européenne sont également en place.</li>
                  <li><strong>Resend :</strong> transferts encadrés par des clauses contractuelles types (CCT) conformément aux décisions d'adéquation de la Commission européenne.</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Ces garanties assurent un niveau de protection des données équivalent à celui offert par le
                  RGPD, conformément aux articles 44 à 49 du règlement.
                </p>

                {/* 10. Sécurité */}
                <h2 id="securite" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  10. Sécurité des Données
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  ACL Gestion met en œuvre des mesures techniques et organisationnelles appropriées pour
                  protéger les données personnelles contre tout accès non autorisé, toute modification,
                  divulgation ou destruction. Ces mesures incluent notamment :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li><strong>Chiffrement en transit :</strong> toutes les communications entre le navigateur de l'Utilisateur et nos serveurs sont chiffrées via le protocole TLS 1.3.</li>
                  <li><strong>Chiffrement au repos :</strong> les données sensibles stockées en base de données sont chiffrées.</li>
                  <li><strong>Authentification sécurisée :</strong> les jetons d'authentification sont signés avec l'algorithme RS256 (JWT asymétrique avec clé RSA).</li>
                  <li><strong>Hachage des mots de passe :</strong> les mots de passe sont hachés avec l'algorithme bcrypt (facteur de coût adaptatif), rendant tout déchiffrement impossible.</li>
                  <li><strong>Contrôle d'accès :</strong> système de rôles et de permissions granulaires limitant l'accès aux données au strict nécessaire.</li>
                  <li><strong>Sauvegardes :</strong> sauvegardes régulières et automatisées des données, stockées de manière chiffrée.</li>
                  <li><strong>Surveillance :</strong> monitoring continu de l'infrastructure pour détecter et répondre rapidement aux incidents de sécurité.</li>
                </ul>

                {/* 11. Modifications */}
                <h2 id="modifications" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  11. Modifications de la Politique de Confidentialité
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  ACL Gestion se réserve le droit de modifier la présente Politique de Confidentialité à tout
                  moment afin de l'adapter aux évolutions législatives, réglementaires ou techniques. En cas
                  de modification substantielle, les Utilisateurs seront informés par e-mail ou par
                  notification au sein de la Plateforme au moins quinze (15) jours avant l'entrée en vigueur
                  des modifications.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  La date de « dernière mise à jour » figurant en haut de cette page indique la date de la
                  dernière révision. L'utilisation continue de la Plateforme après la date d'entrée en vigueur
                  des modifications vaut acceptation de la Politique de Confidentialité révisée.
                </p>

                {/* 12. Contact DPO */}
                <h2 id="contact-dpo" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  12. Contact — Délégué à la Protection des Données
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Pour toute question relative à la protection de vos données personnelles, pour exercer vos
                  droits ou pour signaler un incident de sécurité, vous pouvez contacter notre Délégué à la
                  Protection des Données (DPO) :
                </p>
                <div className="bg-slate-50 rounded-lg p-4 mt-3 text-slate-600">
                  <p><strong>Délégué à la Protection des Données — ACL Gestion</strong></p>
                  <p>E-mail : <a href="mailto:contact@nexus-360.fr" className="text-nexus-600 hover:underline">contact@nexus-360.fr</a></p>
                  <p className="mt-2 text-sm">
                    Nous nous engageons à traiter votre demande dans les meilleurs délais et au plus tard dans
                    un délai d'un (1) mois conformément au RGPD.
                  </p>
                </div>

                {/* CNIL reminder */}
                <div className="mt-10 p-6 bg-slate-50 rounded-xl border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Réclamation auprès de la CNIL</h3>
                  <p className="text-slate-600">
                    Si vous estimez que le traitement de vos données personnelles constitue une violation du
                    RGPD, vous avez le droit d'introduire une réclamation auprès de la Commission Nationale de
                    l'Informatique et des Libertés (CNIL) — 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 —{' '}
                    <a
                      href="https://www.cnil.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-nexus-600 hover:underline"
                    >
                      www.cnil.fr
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confidentialite;
